import type { ClipboardEvent } from 'react';
import React, { useEffect, useRef } from 'react';

import { intlFmt } from '../../lib';
import { TextInput } from '../form';
import type { LanguageProperty } from '../generated/LanguageProperty';
import { isNumericString, removeWhiteSpaces } from '../../util';

export type OtpInputParameters = {
  autoFocusFirstInput?: boolean;
  authenticationCode: string[];
  lengthOfCode: number;
  onChange: (code: string[]) => boolean;
  hasError: boolean;
  ariaLabelOfDigit?: LanguageProperty;
  ariaDescribedByIdOfDigit?: string;
  onBadCodePasted?: (badCode: string) => void;
  disabled?: boolean;
};

export function OtpInput({
  autoFocusFirstInput,
  authenticationCode,
  onChange,
  hasError,
  ariaLabelOfDigit,
  ariaDescribedByIdOfDigit,
  onBadCodePasted,
  disabled = false
}: Readonly<OtpInputParameters>): JSX.Element {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const focusInput = (index: number) => {
    const el = inputRefs.current[index];
    if (el) {
      setTimeout(() => el.focus(), 0);
    }
  };

  const clearValue = (index: number) => {
    const el = inputRefs.current[index];
    if (el) {
      el.value = '';
    }
  };
  const setActivationCodeArray = (text: string, index: number) => {
    const newAuthenticationCode = [...authenticationCode];
    newAuthenticationCode[index] = removeWhiteSpaces(text);
    return newAuthenticationCode;
  };
  const changeActivationCode = (code: string | string[], index?: number) => {
    const newActivationCodeArray =
      typeof code === 'string'
        ? setActivationCodeArray(code, index ?? 0)
        : code;
    const validationTriggered = onChange(newActivationCodeArray);
    if (validationTriggered) {
      focusInput(0);
    }
  };

  const onPasteFunction = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    const content = e.clipboardData.getData('Text');
    const codeAsArray = [];
    for (let i = 0; i < content.length; i++) {
      const digit = content.charAt(i);
      if (isNumericString(digit)) {
        codeAsArray.push(digit);
      }
    }
    if (codeAsArray.length === authenticationCode.length) {
      changeActivationCode(codeAsArray);
    } else if (onBadCodePasted) {
      onBadCodePasted(content);
    }
  };

  // if autoFocusFirstInput is true, focus the first input when it becomes visible
  useEffect(() => {
    if (autoFocusFirstInput && inputRefs && inputRefs.current[0]) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            inputRefs.current[0]?.focus();
            observer.disconnect();
          }
        },
        { threshold: 1.0 }
      );

      observer.observe(inputRefs.current[0]);
      return () => observer.disconnect();
    }
  }, [autoFocusFirstInput]);

  const createOtpInput = (index: number) => {
    return (
      <TextInput
        key={'otp-input' + index}
        ref={(el: HTMLInputElement | null) => {
          inputRefs.current[index] = el;
        }}
        debounceWait={1000}
        label={''}
        value={authenticationCode[index]}
        onFocus={() => {
          if (!isNumericString(authenticationCode[index])) {
            setActivationCodeArray('', index);
          }
        }}
        onChange={() => {
          // empty, to avoid warning
        }}
        onPaste={onPasteFunction}
        onKeyDown={e => {
          if (disabled) {
            return;
          }
          const element = e.target as HTMLInputElement;
          if (isNumericString(e.key)) {
            element.value = e.key;
            changeActivationCode(element.value, index);
            focusInput(index + 1);
          } else if (e.key === 'Backspace') {
            focusInput(index - 1);
            clearValue(index - 1);
            changeActivationCode('', index - 1);
          } else if (e.key === 'Delete') {
            element.value = '';
          } else if (e.key === 'ArrowRight') {
            focusInput(index + 1);
          } else if (e.key === 'ArrowLeft') {
            focusInput(index - 1);
          }
          const isCtrlV = e.key === 'v' && e.ctrlKey;
          if (e.key !== 'Tab' && !isCtrlV) {
            e.preventDefault();
          }
        }}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        className={'otp-input' + index}
        autoFocus={false}
        aria-label={ariaLabelOfDigit && intlFmt(ariaLabelOfDigit, index + 1)}
        aria-describedby={ariaDescribedByIdOfDigit}
        disabled={disabled}
      />
    );
  };

  return (
    <div className={`otp-input-container${hasError ? ' error' : ''}`}>
      {authenticationCode.map((_, i) => createOtpInput(i))}
    </div>
  );
}
